// @ts-strict-ignore
import React, { useContext } from 'react';
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Icon,
  IconName,
} from '@usealloy/component-library';
import AggregationSelect from './AggregationSelect';
import type { Option } from '@usealloy/component-library';
import {
  FILTER_OPERATOR_OPTIONS,
  BOOLEAN_FILTER_OPTIONS,
} from './constants';
import type { AggregationFilter } from 'flow/store/engine/types';
import type { AggregationFilterOption } from '~/dashboard/views/settings/aggregations/types';
import { AggregationFilterOperatorType } from 'flow/store/engine/constants';
import { getColumnKey, resolveDataType } from './helpers';
import {
  StyledFilterCriteriaRow,
  StyledAggregationClause,
  StyledFilterValueInput,
  StyledAggregationNumberInput,
} from './styles';
import { RuleBuilderContext } from '../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/context';
import { AggregationEditorContext } from './context';

type AggregationFilterRowProps = {
  filter: AggregationFilter;
  filterOption?: AggregationFilterOption;
  onUpdate: (filterKey: string, updates: Partial<AggregationFilter>) => void;
  onRemove: (filterKey: string) => void;
};

const defaultStaticValueForOperator = (
  operator: AggregationFilterOperatorType,
): AggregationFilter['staticValue'] => {
  switch (operator) {
    case AggregationFilterOperatorType.BT: return ['', ''];
    case AggregationFilterOperatorType.IN: return [];
    default: return '';
  }
};

// -- Sub-components ---------------------------------------------------------

/** Single value input — renders the appropriate control based on the filter option's data type. */
const FilterDataTypeInput = ({
  value,
  filterOption,
  onChange,
}: {
  value: string;
  filterOption?: AggregationFilterOption;
  onChange: (val: string) => void;
}) => {
  const { multiSelectProps } = useContext(AggregationEditorContext);
  const possibleValues = filterOption?.possible_values;

  // If the filter option has possible values, render a select input with the possible values
  if (possibleValues?.length) {
    return (
      <AggregationSelect
        multiSelectProps={multiSelectProps}
        options={possibleValues.map(v => ({ value: v, label: v }))}
        value={value}
        placeholder="[select]"
        onChange={(option: Option) => onChange(option.value)}
      />
    );
  }

  const uiDataType = resolveDataType(filterOption);

  switch (uiDataType) {
    case 'boolean':
      return (
        <AggregationSelect
          multiSelectProps={multiSelectProps}
          options={BOOLEAN_FILTER_OPTIONS}
          value={value}
          onChange={(option: Option) => onChange(option.value)}
        />
      );

    case 'integer':
    case 'decimal':
      return (
        <StyledAggregationNumberInput
          minValue={0}
          maxValue={Number.MAX_SAFE_INTEGER}
          decimalScale={uiDataType === 'decimal' ? 2 : undefined}
          value={Number(value) || 0}
          onHandleValueChange={(val: number) => onChange(String(val))}
        />
      );
    default:
      return (
        <StyledFilterValueInput
          type="text"
          value={value}
          placeholder="[enter value]"
          onChange={e => onChange(e.target.value)}
        />
      );
  }
};

/** Renders value input(s) based on the operator type. */
const FilterValueInput = ({
  filterKey,
  filter,
  filterOption,
  onUpdate,
}: {
  filterKey: string;
  filter: AggregationFilter;
  filterOption?: AggregationFilterOption;
  onUpdate: AggregationFilterRowProps['onUpdate'];
}) => {
  const { containerType } = useContext(RuleBuilderContext);
  const staticValue = filter.staticValue
    ?? defaultStaticValueForOperator(filter.operatorSlug);

  switch (filter.operatorSlug) {
    case AggregationFilterOperatorType.BT: {
      const [lower = '', upper = ''] = Array.isArray(staticValue)
        ? staticValue
        : ['', ''];
      return (
        <>
          <FilterDataTypeInput
            value={lower}
            filterOption={filterOption}
            onChange={val => onUpdate(filterKey, { staticValue: [val, upper] })}
          />
          <StyledAggregationClause containerType={containerType}>
            and
          </StyledAggregationClause>
          <FilterDataTypeInput
            value={upper}
            filterOption={filterOption}
            onChange={val => onUpdate(filterKey, { staticValue: [lower, val] })}
          />
        </>
      );
    }

    // TODO (OAK-325): render a multi-select constrained to possible_values when available
    case AggregationFilterOperatorType.IN: {
      const values = Array.isArray(staticValue) ? staticValue : [];
      return (
        <StyledFilterValueInput
          type="text"
          value={values.join(', ')}
          placeholder="[comma-separated values]"
          onChange={e => {
            const updated = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
            onUpdate(filterKey, { staticValue: updated });
          }}
        />
      );
    }
    default:
      return (
        <FilterDataTypeInput
          value={typeof staticValue === 'string' ? staticValue : ''}
          filterOption={filterOption}
          onChange={val => onUpdate(filterKey, { staticValue: val })}
        />
      );
  }
};

// -- Main component ---------------------------------------------------------

const AggregationFilterRow = ({
  filter,
  filterOption,
  onUpdate,
  onRemove,
}: AggregationFilterRowProps) => {
  const { containerType } = useContext(RuleBuilderContext);
  const { multiSelectProps } = useContext(AggregationEditorContext);

  const filterKey = getColumnKey(filter);
  const label = filterOption?.label ?? filter.columnSlug;

  return (
    <StyledFilterCriteriaRow>
      <StyledAggregationClause containerType={containerType}>
        {label}
      </StyledAggregationClause>
      <AggregationSelect
        multiSelectProps={multiSelectProps}
        options={FILTER_OPERATOR_OPTIONS}
        value={filter.operatorSlug}
        onChange={(option: Option) => {
          const nextOperator = option.value as AggregationFilterOperatorType;
          onUpdate(filterKey, {
            operatorSlug: nextOperator,
            staticValue: defaultStaticValueForOperator(nextOperator),
          });
        }}
      />
      <FilterValueInput
        filterKey={filterKey}
        filter={filter}
        filterOption={filterOption}
        onUpdate={onUpdate}
      />
      <Button
        size={ButtonSize.xsmall}
        variant={ButtonVariant.tertiary}
        onClick={() => onRemove(filterKey)}
        aria-label={`Remove ${label} filter`}
      >
        <Icon icon={IconName.Trash} size="small" />
      </Button>
    </StyledFilterCriteriaRow>
  );
};

export default AggregationFilterRow;
